import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin, Link as LinkIcon, DollarSign, Target, X, UploadCloud, Loader2, Layers, CheckSquare } from 'lucide-react';
import { useCreateRentalRequest, useUpdateRentalRequest, useRentalRequest } from '@/hooks/useRental';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/numberInput';
import LocationPicker from '@/components/rental/LocationPicker';
import { useUIStore } from '@/store/uiStore';
import { FileUploadDropzone } from '@/components/common';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { imageApi } from '@/api/imageApi';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';

const AREA_UNITS = [
  { value: 'm²', label: 'm²' },
  { value: 'ft²', label: 'ft²' },
];
const CURRENCY = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];

const PROPERTY_TYPES = [
  { value: 'commercial', label: 'Mặt bằng thương mại (Commercial)' },
  { value: 'retail', label: 'Mặt bằng bán lẻ (Retail)' },
  { value: 'kiosk', label: 'Kiosk / Quầy hàng' },
  { value: 'shophouse', label: 'Shophouse' },
  { value: 'villa', label: 'Biệt thự / Villa' },
  { value: 'other', label: 'Khác' },
];

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Nhà hàng' },
  { value: 'cafe', label: 'Quán cà phê / Trà sữa' },
  { value: 'pub', label: 'Bar / Pub / Club' },
  { value: 'fastfood', label: 'Thức ăn nhanh' },
  { value: 'bakery', label: 'Tiệm bánh' },
  { value: 'other', label: 'Khác' },
];

const optionalNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number().optional()
);

const schema = z.object({
  desiredPropertyType: z.string().min(1, 'Vui lòng chọn loại mặt bằng'),
  desiredBusinessType: z.string().min(1, 'Vui lòng chọn loại hình kinh doanh'),
  preferredAddress: z.string({ required_error: 'Vui lòng nhập địa chỉ cụ thể' }).min(1, 'Vui lòng nhập địa chỉ cụ thể'),
  countryId: z.coerce.number().default(1),
  provinceId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Tỉnh/Thành phố' }).min(1, 'Vui lòng chọn Tỉnh/Thành phố'),
  districtId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Quận/Huyện' }).min(1, 'Vui lòng chọn Quận/Huyện'),
  wardId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Phường/Xã' }).min(1, 'Vui lòng chọn Phường/Xã'),
  currencyUnit: z.string().default('VND'),
  areaUnit: z.string().default('m²'),
  budgetMin: z.preprocess((val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number({ required_error: 'Nhập ngân sách tối thiểu' }).min(1, 'Ngân sách tối thiểu phải > 0')),
  budgetMax: z.preprocess((val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number({ required_error: 'Nhập ngân sách tối đa' }).min(1, 'Ngân sách tối đa phải > 0')),
  requiredArea: optionalNumber,
  expectedSeatingCapacity: optionalNumber,
  desiredFloorLevel: optionalNumber,
  desiredMoveInDate: z.string().optional().nullable(),
  requiresStorage: z.boolean().optional().default(false),
  requiresHoodSystem: z.boolean().optional().default(false),
  requiresGasConnection: z.boolean().optional().default(false),
  requiresGreaseTrap: z.boolean().optional().default(false),
  requiresLiquorLicense: z.boolean().optional().default(false),
  requiresEmployeeBathroom: z.boolean().optional().default(false),
  requiresCustomerBathroom: z.boolean().optional().default(false),
  requiresDeliveryEntrance: z.boolean().optional().default(false),
  requiresOutdoorSeating: z.boolean().optional().default(false),
  requiresParking: z.boolean().optional().default(false),
  additionalRequirements: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

interface Props {
  RentalId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

export default function CreateRentalRequestPage({ RentalId: propRentalId, onSuccess, onCancel }: Props = {}) {
  const params = useParams();
  const id = propRentalId || params.id;
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { mutate: create, isPending: isCreating } = useCreateRentalRequest();
  const { mutate: update, isPending: isUpdating } = useUpdateRentalRequest();
  const isPending = isCreating || isUpdating;
  const { addToast } = useUIStore();
  const { data: rawData } = useRentalRequest(id || '');
  const existingData = (rawData as any)?.data ?? rawData;
  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedGeo, setSelectedGeo] = useState<{ lat: string, lon: string, display_name: string } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      countryId: 1, currencyUnit: 'VND', areaUnit: 'm²',
      requiresStorage: false, requiresHoodSystem: false, requiresGasConnection: false,
      requiresGreaseTrap: false, requiresLiquorLicense: false, requiresEmployeeBathroom: false,
      requiresCustomerBathroom: false, requiresDeliveryEntrance: false, requiresOutdoorSeating: false,
      requiresParking: false
    },
  });

  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        desiredPropertyType: existingData.desiredPropertyType || '',
        desiredBusinessType: existingData.desiredBusinessType || '',
        preferredAddress: existingData.preferredAddress || '',
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || 1,
        districtId: existingData.districtId || 1,
        wardId: existingData.wardId || 1,
        budgetMin: existingData.budgetMin,
        budgetMax: existingData.budgetMax,
        currencyUnit: existingData.currencyUnit || 'VND',
        requiredArea: existingData.requiredArea,
        areaUnit: existingData.areaUnit || 'm²',
        expectedSeatingCapacity: existingData.expectedSeatingCapacity,
        desiredFloorLevel: existingData.desiredFloorLevel,
        desiredMoveInDate: existingData.desiredMoveInDate ? existingData.desiredMoveInDate.split('T')[0] : '',
        requiresStorage: existingData.requiresStorage || false,
        requiresHoodSystem: existingData.requiresHoodSystem || false,
        requiresGasConnection: existingData.requiresGasConnection || false,
        requiresGreaseTrap: existingData.requiresGreaseTrap || false,
        requiresLiquorLicense: existingData.requiresLiquorLicense || false,
        requiresEmployeeBathroom: existingData.requiresEmployeeBathroom || false,
        requiresCustomerBathroom: existingData.requiresCustomerBathroom || false,
        requiresDeliveryEntrance: existingData.requiresDeliveryEntrance || false,
        requiresOutdoorSeating: existingData.requiresOutdoorSeating || false,
        requiresParking: existingData.requiresParking || false,
        additionalRequirements: existingData.additionalRequirements || '',
      } as any);

      if (existingData.latitude && existingData.longitude) {
        setSelectedGeo({
          lat: existingData.latitude.toString(),
          lon: existingData.longitude.toString(),
          display_name: existingData.preferredAddress || '',
        });
      }

      if (existingData.images?.length) {
        setImages(
          existingData.images.map((img: any) => ({
            id: img.imageId || img.id,
            url: img.image?.publicUrl || '',
            isPrimary: img.isPrimary,
          }))
        );
      }
    }
  }, [isEditMode, existingData, reset]);

  const countryId = watch('countryId') || 1;
  const provinceId = watch('provinceId');
  const districtId = watch('districtId');
  const { data: provinces } = useProvinces(countryId);
  const { data: districts } = useDistricts(provinceId);
  const { data: wards } = useWards(districtId);

  const onSubmit = async (data: FormData) => {
    if (data.budgetMax !== undefined && data.budgetMin !== undefined && data.budgetMax < data.budgetMin) {
      addToast('error', 'Ngân sách tối đa phải lớn hơn hoặc bằng ngân sách tối thiểu');
      return;
    }

    try {
      setIsUploading(true);
      const finalImages = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res && res.data) {
            finalImages.push({ id: res.data.id, isPrimary: img.isPrimary });
          }
        } else {
          finalImages.push({ id: img.id, isPrimary: img.isPrimary });
        }
      }

      let latitude = selectedGeo?.lat;
      let longitude = selectedGeo?.lon;

      if (!latitude || !longitude) {
        try {
          const provinceName = provinces?.find((p) => p.id === data.provinceId)?.name || '';
          const districtName = districts?.find((d) => d.id === data.districtId)?.name || '';
          const wardName = wards?.find((w) => w.id === data.wardId)?.name || '';

          const fullAddress = [data.preferredAddress, wardName, districtName, provinceName, 'Vietnam']
            .filter(Boolean)
            .join(', ');

          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`, {
            headers: {
              'Accept-Language': 'vi-VN,vi;q=0.9,en-US;q=0.8,en;q=0.7',
            }
          });
          const geoData = await res.json();

          if (geoData && geoData.length > 0) {
            latitude = geoData[0].lat;
            longitude = geoData[0].lon;
          }
        } catch (err) {
          console.error('Failed to geocode address', err);
        }
      }

      const payload = {
        ...data,
        budgetMin: Number(data.budgetMin),
        budgetMax: Number(data.budgetMax),
        requiredArea: Number(data.requiredArea || 0),
        images: finalImages,
        desiredMoveInDate: data.desiredMoveInDate ? new Date(data.desiredMoveInDate).toISOString() : null,
        latitude,
        longitude,
      };

      if (isEditMode && id) {
        update({ id, data: payload as any }, {
          onSuccess: () => {
            if (onSuccess) onSuccess();
            else navigate(`/nhu-cau-cho-thue/${id}`);
          }
        });
      } else {
        create(payload as any, {
          onSuccess: (res: any) => {
            if (onSuccess) onSuccess();
            else navigate(`/nhu-cau-cho-thue`);
          }
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        addToast('error', 'Ảnh tải lên quá lớn (vượt giới hạn 1MB của máy chủ). Vui lòng chọn ảnh nhỏ hơn.');
      } else {
        addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.log(errors);
    addToast('error', 'Vui lòng kiểm tra lại các trường thông tin bị thiếu hoặc không hợp lệ.');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newLocalImages: UploadedImg[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        addToast('error', `File ${file.name} không phải là hình ảnh hợp lệ.`);
        continue;
      }

      if (file.size > 1 * 1024 * 1024) {
        addToast('error', `Ảnh ${file.name} vượt quá dung lượng cho phép. Vui lòng chọn ảnh dưới 1MB.`);
        continue;
      }

      newLocalImages.push({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0 && newLocalImages.length === 0,
        file,
      });
    }

    setImages((prev) => [...prev, ...newLocalImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      if (filtered.length > 0 && !filtered.find((img) => img.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-10">
      <div className="flex items-center gap-3 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl" style={{ color: 'var(--text-1)' }}>
            {isEditMode ? 'Cập nhật nhu cầu thuê' : 'Đăng nhu cầu thuê mặt bằng'}
          </h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-3)' }}>
            Điền đầy đủ thông tin để tìm được mặt bằng phù hợp nhất
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {/* ── Thông tin chung ── */}
        <Card title="Thông tin chung" icon={<Target size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Controller
              name="desiredPropertyType"
              control={control}
              render={({ field }) => (
                <Select
                  label="Loại mặt bằng mong muốn *"
                  options={PROPERTY_TYPES}
                  error={errors.desiredPropertyType?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="desiredBusinessType"
              control={control}
              render={({ field }) => (
                <Select
                  label="Loại hình kinh doanh *"
                  options={BUSINESS_TYPES}
                  error={errors.desiredBusinessType?.message}
                  {...field}
                />
              )}
            />
          </div>
        </Card>

        {/* ── Ngân sách & Yêu cầu không gian ── */}
        <Card title="Ngân sách & Yêu cầu không gian" icon={<DollarSign size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Controller
              name="budgetMin"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Ngân sách tối thiểu"
                  placeholder="VD: 10,000,000"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.budgetMin?.message}
                />
              )}
            />
            <Controller
              name="budgetMax"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Ngân sách tối đa (VNĐ)"
                  placeholder="VD: 20,000,000"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.budgetMax?.message}
                />
              )}
            />
            <Controller
              name="requiredArea"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Diện tích cần thiết"
                  placeholder="VD: 100"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.requiredArea?.message}
                />
              )}
            />
            <Controller
              name="expectedSeatingCapacity"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Sức chứa dự kiến"
                  placeholder="VD: 50"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.expectedSeatingCapacity?.message}
                />
              )}
            />
            <Controller
              name="desiredFloorLevel"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <NumberInput
                  label="Tầng mong muốn"
                  placeholder="VD: 1"
                  value={value}
                  onChange={onChange}
                  error={errors.desiredFloorLevel?.message}
                />
              )}
            />
            <Input
              type="date"
              label="Ngày dự kiến chuyển vào"
              {...register('desiredMoveInDate')}
              error={errors.desiredMoveInDate?.message}
            />
          </div>
        </Card>

        {/* ── Tiện ích yêu cầu ── */}
        <Card title="Tiện ích & Yêu cầu kỹ thuật" icon={<CheckSquare size={18} />}>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
            {[
              { name: 'requiresStorage', label: 'Cần kho chứa đồ' },
              { name: 'requiresHoodSystem', label: 'Hệ thống hút khói' },
              { name: 'requiresGasConnection', label: 'Đường ống gas' },
              { name: 'requiresGreaseTrap', label: 'Bẫy mỡ' },
              { name: 'requiresLiquorLicense', label: 'Giấy phép rượu' },
              { name: 'requiresEmployeeBathroom', label: 'Toilet nhân viên' },
              { name: 'requiresCustomerBathroom', label: 'Toilet khách hàng' },
              { name: 'requiresDeliveryEntrance', label: 'Lối giao hàng riêng' },
              { name: 'requiresOutdoorSeating', label: 'Khu vực ngoài trời' },
              { name: 'requiresParking', label: 'Bãi đỗ xe' },
            ].map((feature) => (
              <Controller
                key={feature.name}
                name={feature.name as any}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={<Checkbox checked={!!value} onChange={onChange} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
                    label={<span className="text-sm font-medium">{feature.label}</span>}
                  />
                )}
              />
            ))}
          </div>
        </Card>

        {/* ── Khu vực mong muốn ── */}
        <Card title="Vị trí mong muốn" icon={<MapPin size={18} />}>
          <LocationPicker
            addressField="preferredAddress"
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            selectedGeo={selectedGeo}
            onSelectGeo={setSelectedGeo}
          />
        </Card>

        {/* ── Yêu cầu thêm ── */}
        <Card title="Ghi chú thêm" icon={<Layers size={18} />}>
          <Textarea
            label="Các yêu cầu khác"
            rows={4}
            placeholder="VD: Cần hỗ trợ thi công, thời gian thuê tối thiểu 3 năm..."
            {...register('additionalRequirements')}
            error={errors.additionalRequirements?.message}
          />
        </Card>

        {/* ── Hình ảnh minh hoạ ── */}
        <Card title="Hình ảnh mô phỏng / Concept (Tuỳ chọn)" icon={<LinkIcon size={18} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn hoặc kéo thả ảnh concept vào đây"
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={cn(
                    "relative aspect-video rounded-xl overflow-hidden border-2 group",
                    img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"
                  )}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(img.id)}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4 justify-end pt-4 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <Button type="button" variant="ghost" onClick={() => onCancel ? onCancel() : navigate('/nhu-cau-cho-thue')} className="px-8 font-bold">
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending || isUploading} className="px-8 font-bold text-base shadow-lg shadow-orange-500/20">
            {isEditMode ? 'Lưu thay đổi' : 'Đăng nhu cầu'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border p-6 md:p-8 space-y-4 bg-white shadow-sm" style={{ borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-lg flex items-center gap-2 pb-4 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
          {icon}
        </div>
        {title}
      </h2>
      <div className="pt-2">
        {children}
      </div>
    </div>
  );
}
